import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container, Card, CardBody, Button } from 'reactstrap';
import browserLocation from '../../utils/browserLocation';
import { getItem, setItem } from '../../utils/localStorage';

// Survives a reload so we can detect a render error that keeps recurring and
// avoid an infinite refresh loop on a deterministic boot-time crash.
const REFRESH_KEY = 'errorBoundaryRefreshCount';
const MAX_REFRESH = 2;
const COPIED_RESET_MS = 2000;
class ErrorBoundary extends Component {
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      copyStatus: 'idle',
      refreshExhausted: false
    };
    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleCopyDiagnostics = this.handleCopyDiagnostics.bind(this);
  }
  componentDidCatch(error, info) {
    this.setState({
      info
    });
    // No centralized telemetry/logError utility exists in web yet, and the
    // shared logger only logs in dev/docker. console.error is the only trace
    // available in production. Replace with real telemetry as a follow-up.
    /* eslint-disable no-console */
    console.error('ErrorBoundary caught an error:', error, info);
    /* eslint-enable no-console */
  }
  componentWillUnmount() {
    if (this.copyTimer) {
      global.clearTimeout(this.copyTimer);
    }
  }
  handleRefresh() {
    const parsed = Number(getItem(REFRESH_KEY, '0'));
    const count = Number.isFinite(parsed) ? parsed : 0;
    if (count >= MAX_REFRESH) {
      this.setState({
        refreshExhausted: true
      });
      return;
    }
    setItem(REFRESH_KEY, String(count + 1));
    browserLocation.reload();
  }
  handleCopyDiagnostics() {
    var _global$navigator$cli;
    const _this$state = this.state,
      error = _this$state.error,
      info = _this$state.info;
    const payload = {
      url: global.location.href,
      userAgent: global.navigator.userAgent,
      name: error === null || error === void 0 ? void 0 : error.name,
      message: error === null || error === void 0 ? void 0 : error.message,
      stack: error === null || error === void 0 ? void 0 : error.stack,
      componentStack: info === null || info === void 0 ? void 0 : info.componentStack,
      time: new Date().toISOString()
    };
    if ((_global$navigator$cli = global.navigator.clipboard) !== null && _global$navigator$cli !== void 0 && _global$navigator$cli.writeText) {
      global.navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
        this.setCopyStatus('copied');
      }).catch(() => {
        this.setCopyStatus('failed');
      });
    } else {
      this.setCopyStatus('failed');
    }
  }
  setCopyStatus(copyStatus) {
    this.setState({
      copyStatus
    });
    if (this.copyTimer) {
      global.clearTimeout(this.copyTimer);
    }
    this.copyTimer = global.setTimeout(() => {
      this.setState({
        copyStatus: 'idle'
      });
    }, COPIED_RESET_MS);
  }
  render() {
    const _this$state2 = this.state,
      hasError = _this$state2.hasError,
      copyStatus = _this$state2.copyStatus,
      refreshExhausted = _this$state2.refreshExhausted;
    const children = this.props.children;
    if (hasError) {
      let copyLabel = 'Copy diagnostics';
      if (copyStatus === 'copied') {
        copyLabel = 'Copied!';
      } else if (copyStatus === 'failed') {
        copyLabel = 'Copy failed';
      }
      return /*#__PURE__*/React.createElement(Container, {
        className: "mt-5"
      }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardBody, {
        className: "text-center p-5"
      }, /*#__PURE__*/React.createElement("h2", null, "Something went wrong"), /*#__PURE__*/React.createElement("p", {
        className: "mt-3"
      }, refreshExhausted ? "Refreshing did not resolve the problem. Please copy the\n                     diagnostics below and contact support." : "An unexpected error occurred. Please try refreshing the\n                     page. If the problem persists, contact support and include\n                     the diagnostics copied below."), /*#__PURE__*/React.createElement("div", {
        className: "mt-4"
      }, /*#__PURE__*/React.createElement(Button, {
        color: "primary",
        className: "mr-3",
        disabled: refreshExhausted,
        onClick: this.handleRefresh
      }, "Refresh"), /*#__PURE__*/React.createElement(Button, {
        color: "secondary",
        onClick: this.handleCopyDiagnostics
      }, copyLabel)))));
    }
    return children;
  }
}
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};
export default ErrorBoundary;